import React, {Component} from 'react';
import { withTranslation, Trans } from 'react-i18next';
import FlowForm, { FlowPageContent, FlowQuestionContainer, AutoAdvanceButton, FlowSubtext, FullWidthNextButton, FixedSizeQuestion } from 'components/FlowForm';
import { FlowAnswerButton } from 'components/Button';
import { allMainMatches, workoutOnboardBaseFor, workoutPlanPath, workoutPreviewMatch, workoutPreviewPathFor } from 'config/paths';
import { formikHandlerCreator } from 'lib/utilities';
import { dismissTooltip } from 'redux/actions';
import { userRecordSelector } from 'redux/selectors'
import { connect } from 'react-redux';
import Page from 'components/Page';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MpsChart } from 'partials/EstimatedProgressChart';
import { Illustration } from 'partials/OnboardingComponents';
import * as transitions from 'assets/transitions';
import workoutScience from 'assets/img/workout_science.webp';
import { WORKOUTS_ONBOARD_TIP } from 'config/tooltips';
import { getCurrentLocale } from 'lib/i18n-service';
import * as _ from 'lib/utilities';

const InitialScreen = ({ user, flowProps, t }) => {
    const text = user.weightGoal === 1 ? t('briefintrogain') : t('briefintroloss');

    return (
        <React.Fragment>
            <FixedSizeQuestion className="mt0 pt0" text={t('briefintro', { text })} />
            <FlowSubtext className="mb20">
                {t("briefintrosub")}
            </FlowSubtext>
            <Illustration src={workoutScience} width={480} height={480} />
            <FullWidthNextButton 
                flowProps={flowProps}
                label={t('Continue')}
            />
        </React.Fragment>
    )
}

const SetsAndRepsQ = ({ flowProps, t }) => {

    return (
        <React.Fragment>
            <FixedSizeQuestion text={t("setsandrepsq")} />
            {["Yes","No"].map((answer,i) => {
                
                return (
                    <AutoAdvanceButton key={i} {...flowProps} attr="knowsSetsAndReps" value={answer} render={({ onClick }) => (
                        <FlowAnswerButton color="primary" rounded onClick={onClick} >
                            <span>{t(answer)}</span>
                        </FlowAnswerButton>
                    )} />
                )
            })}
        </React.Fragment>
    )
}

const SetsAndRepsExp = ({ user, flowProps, t }) => {
    const cta = user.weightGoal === 1 ? t('briefintrogain') : t('briefintroloss');
    const locale = getCurrentLocale();

    return (
        <React.Fragment>
            <FixedSizeQuestion className="mt0 pt0" text={t('setsandrepsare')} />
            <Illustration src={`${_.publicUrl()}img/locales/${locale}/sets_reps_infographic.webp`} width={480} height={480} />
            <FlowSubtext className="text-left mt20 mb10 ml10">
                {t("setsandrepssmooth")}
            </FlowSubtext>
            <FullWidthNextButton 
                flowProps={flowProps}
                label={t(cta)}
                icon={'arrow-right'}
            />
        </React.Fragment>
    )
}

const WorkoutsWhyScreen = ({ user, flowProps, t }) => {
    const descs = user.weightGoal === 1 ? ['workoutwhy4','workoutwhy5'] : ['workoutwhy2','workoutwhy3'];
    const img = `mps${user.weightGoal === 1 ? '_gain' : ''}${user.female() ? '_f' : '_m'}`;
    const mpsAcronym = t('muscle_protein_synthesis_acronym');
    const locale = getCurrentLocale();

    return (
        <React.Fragment>
            <FixedSizeQuestion className="mt0 pt0" text={t('workoutsonbkey', { muscle_protein_synthesis_acronym: mpsAcronym })} />
            <Illustration src={`${_.publicUrl()}img/locales/${locale}/${img}.webp`} width={480} height={507} />
            <div className="text-left">
                <FlowSubtext className="mb10"><Trans i18nKey={'workoutwhy1'} values={{ muscle_protein_synthesis_acronym: mpsAcronym }}><b></b></Trans></FlowSubtext>
                {descs.map(desc => (<FlowSubtext className="mb10"><Trans i18nKey={desc} values={{ muscle_protein_synthesis_acronym: mpsAcronym }}><b></b></Trans></FlowSubtext>))}
            </div>

            <FullWidthNextButton
                flowProps={flowProps}
                label={t('Continue')}
            />
        </React.Fragment>
    )
}

const MpsIllustration = ({ t }) => {
    const mpsAcronym = t('muscle_protein_synthesis_acronym');

    return (
        <Illustration>
            <MpsChart />
            <div className="title">{t("mpsovertime", { muscle_protein_synthesis_acronym: mpsAcronym })}</div>
            <div className="bot-axis-lbl ll">{t('Day X', { day: 1 })}</div>
            <div className="bot-axis-lbl rl">{t('Day X', { day: 7 })}</div>
            <div className="rdiets-label">
                {t("fatstorethresh")}
            </div>
            <div className="fat-store-thresh"></div>
        </Illustration>
    )
}

const AllItTakesScreen = ({ user, flowProps, t }) => {
    let type = t('15minworkout');
    let setNum = '1-2';
    let workoutLen = '15-30';
    if(user.liftingExperience > 0) {
        if((user.workoutTime || 0) >= 1800) {
            type = t('30minworkout');
            workoutLen = '30-45';
        }
        setNum = '2-4';
    }
    const mpsAcronym = t('muscle_protein_synthesis_acronym');

    return (
        <React.Fragment>
            <FixedSizeQuestion className="mt0 pt0" text={t('shortworkoutsfewdays', { type, muscle_protein_synthesis_acronym: mpsAcronym })} />
            <MpsIllustration t={t} />
            <div className="text-left">
                <FlowSubtext className="mb10"><Trans i18nKey={'mpslasts'} values={{ muscle_protein_synthesis_acronym: mpsAcronym }}><b></b></Trans></FlowSubtext>
                <FlowSubtext className="mb10">{t('allmpstakes', { num: setNum, muscle_protein_synthesis_acronym: mpsAcronym })}</FlowSubtext>
                <FlowSubtext className="mb10">{t('mpsgetitdone', { mins: workoutLen })}</FlowSubtext>
            </div>

            <FullWidthNextButton
                flowProps={flowProps}
                label={t('Continue')}
            />
        </React.Fragment>
    )
}


const RepAndProgressScreen = ({ user, flowProps, t }) => {
    const descs = ["progimpoworep","repgreaterthanvar"];
    const mpsAcronym = t('muscle_protein_synthesis_acronym');
    const locale = getCurrentLocale();

    return (
        <React.Fragment>
            <FixedSizeQuestion className="mt0 pt0" text={t('repandprogress')} />
            <Illustration src={`${_.publicUrl()}img/locales/${locale}/repetition.webp`} width={480} height={320} />
            <div className="text-left">
                <FlowSubtext className="mb10"><Trans i18nKey={"progwtrepsmps"} values={{ muscle_protein_synthesis_acronym: mpsAcronym }}><b></b></Trans></FlowSubtext>
                {descs.map(desc => (<FlowSubtext className="mb10"><Trans i18nKey={desc}><b></b></Trans></FlowSubtext>))}
            </div>

            <FullWidthNextButton
                flowProps={flowProps}
                label={t('Continue')}
            />
        </React.Fragment>
    )
}

const summaryDonts = [
    { icon: 'times', desc: 'dontneedmps1' },
    { icon: 'times', desc: 'dontneedmps2' },
    { icon: 'times', desc: 'dontneedmps3' },
    { icon: 'times', desc: 'dontneedmps4' }
]

const summaryDos = [
    { icon: ['far','clock'], desc: 'thatswhymps1' },
    { icon: 'redo', desc: 'thatswhymps2' },
    { icon: ['fal','chart-line'], desc: 'thatswhymps3' }
]

const FinalSummaryScreen = ({ user, flowProps, t, dismissTooltip, history, date }) => {
    const mpsAcronym = t('muscle_protein_synthesis_acronym');

    return (
        <React.Fragment>
            <div>
                <FixedSizeQuestion className="mb15 mt25 pt0 text-left" text={t('dontneedmps', { muscle_protein_synthesis_acronym: mpsAcronym })} />
                <div>
                    <div className="onb-ol">
                        {summaryDonts.map(({ desc, icon },i) => {

                            if(user.needsCardio && i === summaryDonts.length-1) {
                                return '';
                            }

                            return (
                                <div className="onb-ol-li pt5 pb5 cntr" key={i}>
                                    <div className="icon bad">
                                        <FontAwesomeIcon icon={icon} />
                                    </div>
                                    <div className="title-cont">
                                        <label>{t(desc)}</label>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
            <div>
                <FixedSizeQuestion className="mb15 mt25 pt0 text-left" text={t('thatswhymps')} />
                <div>
                    <div className="onb-ol">
                        {summaryDos.map(({ desc, icon },i) => {

                            return (
                                <div className="onb-ol-li pt5 pb5 cntr" key={i}>
                                    <div className="icon">
                                        <FontAwesomeIcon icon={icon} />
                                    </div>
                                    <div className="title-cont">
                                        <label>{t(desc)}</label>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>

            <FullWidthNextButton
                flowProps={{ ...flowProps, advance: values =>  {
                    dismissTooltip(WORKOUTS_ONBOARD_TIP);
                    history.replace(workoutPreviewPathFor(date))
                }}}
                label={t('Got It')}
            />
        </React.Fragment>
    )
}

const getNextPage = (page,values) => {
    switch(pageToComp(page)) {
        case SetsAndRepsQ: {
            if(values.knowsSetsAndReps === 'Yes') {
                return page+2;
            }
            return page+1;
        }
        default: {
            return page+1;
        }
    }
}

const transitionMap = {
    rules: [
        [[...allMainMatches,workoutPreviewMatch],transitions.slideOut]
    ]
};

const fullFlow = [
    InitialScreen,
    SetsAndRepsQ,
    SetsAndRepsExp,
    WorkoutsWhyScreen,
    AllItTakesScreen,
    RepAndProgressScreen,
    FinalSummaryScreen
]

const pageToComp = (page) => {
    return fullFlow[page-1];
};

class WorkoutsOnboardingPage extends Component {

    constructor(props) {
        super(props);
        this.state = { loadState: 'DEFAULT', errorMsg: null };
        this.setSubmitState = this.setSubmitState.bind(this);
        props.setupTransitions(transitionMap);
        console.log('here')
    }

    setSubmitState(status,formikBag,data) {
        this.setState({ loadState: status, errorMsg: null });
    }

    render() {
        let { 
            t, 
            user, 
            scrollRef, 
            dismissTooltip,
            match: { params: { date } },
            history
        } = this.props;

        return (
            <Page white ref={scrollRef} className="fixed-next-flow">
                <FlowForm 
                    doReplace={true}
                    initialValues={{ knowsSetsAndReps: 'Yes' }}
                    validate={() => ({})}
                    validateOnMount={false}
                    basePath={workoutOnboardBaseFor(date)} 
                    nextPage={getNextPage}
                    highestValidPageFn={() => 1}
                    exitPath={workoutPlanPath}
                    startIndex={1}
                    advanceCallback={data => {}}
                    onSubmit={formikHandlerCreator(this,this.setSubmitState,() => Promise.resolve({ status: "SUCCESS"}))}
                >
                    {fullFlow.map((Comp,pg) => {
                        return (
                            <FlowPageContent key={pg} render={(flowProps) => {
                                return (
                                    <FlowQuestionContainer>
                                        <Comp flowProps={flowProps} t={t} user={user} dismissTooltip={dismissTooltip} history={history} date={date} />
                                    </FlowQuestionContainer>
                                )
                            }} />
                        )
                    })}
                </FlowForm>
            </Page>
        );
    }

    componentWillUnmount() {
        if(this.loadPromise){
            this.loadPromise.cancel();
        }
    }
}

//be careful reading state here because newEmailUser wipes all data and reloads user show view
//which caused multiple bugs with bannable progs not being loaded when expected
const mapStateToProps = state => ({
    user: userRecordSelector(state)
})

const mapDispatchToProps = dispatch => ({
    dismissTooltip: tip => dispatch(dismissTooltip(tip))
})

export default connect(mapStateToProps,mapDispatchToProps)(withTranslation()(WorkoutsOnboardingPage));
